
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React from 'react';
import { Card } from '../../ui/Card';
import { Download, Check } from 'lucide-react';
import { GhostBrandIcon } from '../../ui/PartnerIcons';

export const BrandGuide: React.FC = () => {
    return (
        <div className="space-y-12 not-prose">
            <div>
                <h2 className="text-3xl font-serif text-[#0A0B0F] dark:text-white mb-4">Brand Assets</h2>
                <p className="text-stone-600 dark:text-stone-300 leading-relaxed text-lg">
                    GhostPay is a comprehensive <strong>Payment Gateway</strong> integrated into eCommerce, operating the fundamental payment layer for the decentralized web. Our brand identity reflects this role—combining privacy, security, and luxury.
                </p>
            </div>

            {/* Logo Architecture */}
            <section>
                <div className="flex items-center justify-between mb-8">
                    <h3 className="font-bold text-xl text-[#0A0B0F] dark:text-white font-serif">Logo Architecture</h3>
                    <span className="px-3 py-1 rounded-full bg-stone-100 dark:bg-white/10 text-xs font-bold uppercase tracking-wider text-stone-500">1.0.0 Beta</span>
                </div>
                
                <div className="grid grid-cols-1 gap-12">
                    {/* Primary Lockups (Horizontal) */}
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-100 dark:border-white/5 pb-2">Primary Lockup (Horizontal)</h4>
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Light Mode */}
                            <Card className="flex items-center justify-center py-16 bg-white border-stone-200">
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14">
                                        <GhostBrandIcon className="w-full h-full" />
                                    </div>
                                    <div className="flex flex-col justify-center">
                                        <h1 className="font-serif text-3xl font-bold tracking-tight text-[#0A0B0F] leading-none">GHOST</h1>
                                        <div className="text-[10px] text-gray-500 uppercase tracking-[0.35em] font-bold mt-1.5 ml-0.5">PROTOCOL</div>
                                    </div>
                                </div>
                            </Card>
                            {/* Dark Mode */}
                            <Card className="flex items-center justify-center py-16 bg-[#0A0B0F] border-stone-800">
                                 <div className="flex items-center gap-5">
                                    <div className="w-14 h-14">
                                        <GhostBrandIcon className="w-full h-full" />
                                    </div>
                                    <div className="flex flex-col justify-center">
                                        <h1 className="font-serif text-3xl font-bold tracking-tight text-white leading-none">GHOST</h1>
                                        <div className="text-[10px] text-gray-400 uppercase tracking-[0.35em] font-bold mt-1.5 ml-0.5">PROTOCOL</div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>

                    {/* Secondary Stack (Vertical) */}
                    <div>
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-100 dark:border-white/5 pb-2">Vertical Stack (Centered)</h4>
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Light Mode */}
                            <Card className="flex items-center justify-center py-20 bg-white border-stone-200">
                                <div className="flex flex-col items-center gap-8">
                                    <div className="w-20 h-20">
                                        <GhostBrandIcon className="w-full h-full drop-shadow-2xl" />
                                    </div>
                                    <div className="flex flex-col items-center text-center">
                                        <h1 className="font-serif text-4xl font-bold tracking-tight text-[#0A0B0F] leading-none mb-2">GHOST</h1>
                                        <div className="text-xs text-gray-500 uppercase tracking-[0.4em] font-bold pl-1">PAYMENT OS</div>
                                    </div>
                                </div>
                            </Card>
                            {/* Dark Mode */}
                            <Card className="flex items-center justify-center py-20 bg-[#0A0B0F] border-stone-800">
                                 <div className="flex flex-col items-center gap-8">
                                    <div className="w-20 h-20">
                                        <GhostBrandIcon className="w-full h-full drop-shadow-2xl" />
                                    </div>
                                    <div className="flex flex-col items-center text-center">
                                        <h1 className="font-serif text-4xl font-bold tracking-tight text-white leading-none mb-2">GHOST</h1>
                                        <div className="text-xs text-gray-400 uppercase tracking-[0.4em] font-bold pl-1">PAYMENT OS</div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Logomark (Symbol) */}
            <section>
                <h3 className="font-bold text-xl text-[#0A0B0F] dark:text-white mb-6 font-serif border-t border-stone-200 dark:border-white/10 pt-8">Logomark</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="flex items-center justify-center py-16 bg-white border-stone-200">
                        <div className="w-24 h-24">
                            <GhostBrandIcon className="w-full h-full" />
                        </div>
                    </Card>
                    <Card className="flex items-center justify-center py-16 bg-[#0A0B0F] border-stone-800">
                        <div className="w-24 h-24">
                            <GhostBrandIcon className="w-full h-full" />
                        </div>
                    </Card>
                    <Card className="flex items-center justify-center py-16 bg-nobel-gold border-transparent">
                        <div className="w-24 h-24 brightness-0 invert">
                            <GhostBrandIcon className="w-full h-full" />
                        </div>
                    </Card>
                </div>
                <p className="text-xs text-stone-500 mt-4">
                    The standalone Ghost symbol is used for social media avatars, favicons, and small-scale representations where the full wordmark is illegible.
                </p>
            </section>

            {/* Product Identity (GhostOS & Dashboard) */}
            <section>
                <h3 className="font-bold text-xl text-[#0A0B0F] dark:text-white mb-6 font-serif border-t border-stone-200 dark:border-white/10 pt-8">Product Identity</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* GhostOS */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-end">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">GhostOS (Runtime)</h4>
                            <span className="text-[10px] text-gray-400">System Core</span>
                        </div>
                        <Card className="flex items-center justify-center py-12 bg-white border-stone-200">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12">
                                    <GhostBrandIcon className="w-full h-full" />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <h1 className="font-serif text-2xl font-bold tracking-tight text-[#0A0B0F] leading-none">
                                        <span className="font-light">Ghost</span>OS
                                    </h1>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Ghost Dashboard */}
                    <div className="space-y-4">
                        <div className="flex justify-between items-end">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">Ghost Dashboard (Enterprise)</h4>
                            <span className="text-[10px] text-gray-400">Management Suite</span>
                        </div>
                        <Card className="flex items-center justify-center py-12 bg-[#0A0B0F] border-stone-800">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12">
                                    <GhostBrandIcon className="w-full h-full" />
                                </div>
                                <div className="flex flex-col justify-center">
                                    <h1 className="font-serif text-2xl font-bold tracking-tight text-white leading-none">
                                        <span className="font-light">Ghost</span><span className="font-bold">Dashboard</span>
                                    </h1>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* App Icons */}
            <section>
                <h3 className="font-bold text-xl text-[#0A0B0F] dark:text-white mb-8 font-serif border-t border-stone-200 dark:border-white/10 pt-8">App Icons</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
                    {[
                        { bg: 'bg-[#0A0B0F]', label: 'Midnight (Default)', border: 'border-white/10' },
                        { bg: 'bg-white', label: 'Snow', border: 'border-stone-200' },
                        { bg: 'bg-[#C5A059]', label: 'Nobel Gold', border: 'border-transparent' },
                        { bg: 'bg-[#2D3142]', label: 'Slate', border: 'border-transparent' },
                    ].map((icon, i) => (
                        <div key={i} className="flex flex-col items-center gap-4 group cursor-pointer">
                            <div className={`w-32 h-32 rounded-[28px] ${icon.bg} flex items-center justify-center shadow-2xl border ${icon.border} group-hover:scale-105 transition-transform duration-300 relative overflow-hidden`}>
                                <div className={`w-20 h-20 ${icon.label === 'Snow' || icon.label === 'Nobel Gold' ? 'brightness-0' : ''}`}>
                                    <GhostBrandIcon className="w-full h-full drop-shadow-xl" />
                                </div>
                            </div>
                            <span className="text-xs font-mono text-gray-500 group-hover:text-nobel-gold transition-colors text-center">{icon.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* Payment Trust Marks */}
            <section>
                <h3 className="font-bold text-xl text-[#0A0B0F] dark:text-white mb-8 font-serif border-t border-stone-200 dark:border-white/10 pt-8">Payment Trust Marks</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Payment Icon (G Logo with Payment styling) */}
                    <div className="flex flex-col items-center justify-center gap-4">
                         <div className="w-32 h-32 rounded-[28px] bg-[#0A0B0F] flex items-center justify-center relative border border-white/10 shadow-2xl">
                            <div className="w-20 h-20">
                                <GhostBrandIcon className="w-full h-full" />
                            </div>
                            <div className="absolute bottom-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-[#0A0B0F] shadow-lg border-2 border-[#0A0B0F]">
                                <Check size={16} strokeWidth={4} />
                            </div>
                        </div>
                        <span className="text-xs font-mono text-gray-500 text-center">Gateway Icon</span>
                    </div>

                    {/* Pay with Ghost Button */}
                    <div className="flex flex-col items-center justify-center gap-4">
                        <button className="flex items-center gap-3 px-8 py-4 bg-[#0A0B0F] text-white rounded-xl font-bold text-lg shadow-lg hover:bg-black transition-colors w-full justify-center transform hover:scale-[1.02] duration-200">
                            <div className="w-8 h-8">
                                <GhostBrandIcon className="w-full h-full" />
                            </div>
                            Pay with Ghost
                        </button>
                        <span className="text-xs font-mono text-gray-500 text-center">Checkout Integration</span>
                    </div>

                    {/* Secured Badge */}
                    <div className="flex flex-col items-center justify-center gap-4 h-full">
                         <div className="flex items-center gap-3 px-5 py-2.5 rounded-full border border-stone-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm">
                            <div className="w-6 h-6">
                                <GhostBrandIcon className="w-full h-full" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[8px] font-bold text-stone-400 uppercase tracking-wider leading-none mb-0.5">Secured by</span>
                                <span className="text-xs font-bold text-stone-900 dark:text-white uppercase tracking-wider leading-none">GhostPay</span>
                            </div>
                        </div>
                        <span className="text-xs font-mono text-gray-500 text-center">Trust Badge</span>
                    </div>
                </div>
            </section>
            
            {/* Color Palette */}
            <section>
                <h3 className="font-bold text-xl text-[#0A0B0F] dark:text-white mb-6 font-serif border-t border-stone-200 dark:border-white/10 pt-8">Color System</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-3 group">
                        <div className="h-24 rounded-xl bg-[#0A0B0F] shadow-lg border border-white/5 group-hover:scale-105 transition-transform"></div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-[#0A0B0F] dark:text-white">Ghost Jet</div>
                            <div className="text-[10px] font-mono text-gray-500 mt-0.5">#0A0B0F</div>
                            <div className="text-[10px] text-gray-400 mt-1">Primary Background</div>
                        </div>
                    </div>
                    <div className="space-y-3 group">
                        <div className="h-24 rounded-xl bg-[#C5A059] shadow-lg group-hover:scale-105 transition-transform"></div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-[#0A0B0F] dark:text-white">Nobel Gold</div>
                            <div className="text-[10px] font-mono text-gray-500 mt-0.5">#C5A059</div>
                            <div className="text-[10px] text-gray-400 mt-1">Primary Accent</div>
                        </div>
                    </div>
                    <div className="space-y-3 group">
                        <div className="h-24 rounded-xl bg-[#F8F9FA] border border-gray-200 shadow-sm group-hover:scale-105 transition-transform"></div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-[#0A0B0F] dark:text-white">Off White</div>
                            <div className="text-[10px] font-mono text-gray-500 mt-0.5">#F8F9FA</div>
                            <div className="text-[10px] text-gray-400 mt-1">Secondary Background</div>
                        </div>
                    </div>
                    <div className="space-y-3 group">
                        <div className="h-24 rounded-xl bg-[#2D3142] shadow-lg group-hover:scale-105 transition-transform"></div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-[#0A0B0F] dark:text-white">Coral Slate</div>
                            <div className="text-[10px] font-mono text-gray-500 mt-0.5">#2D3142</div>
                            <div className="text-[10px] text-gray-400 mt-1">Surface Color</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Typography */}
            <section>
                 <h3 className="font-bold text-xl text-[#0A0B0F] dark:text-white mb-6 font-serif border-t border-stone-200 dark:border-white/10 pt-8">Typography</h3>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <Card>
                         <div className="text-6xl font-serif text-[#0A0B0F] dark:text-white mb-2">Aa</div>
                         <div className="font-bold text-lg text-[#0A0B0F] dark:text-white">Playfair Display</div>
                         <p className="text-xs text-gray-500 mt-1">Headings & Display</p>
                     </Card>
                     <Card>
                         <div className="text-6xl font-sans text-[#0A0B0F] dark:text-white mb-2">Aa</div>
                         <div className="font-bold text-lg text-[#0A0B0F] dark:text-white">Inter</div>
                         <p className="text-xs text-gray-500 mt-1">Body & Interface</p>
                     </Card>
                 </div>
            </section>

            <section>
                <Card className="bg-[#0A0B0F] border-stone-800">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-4">
                        <div className="text-white">
                            <div className="font-serif font-bold text-xl mb-1">Download Media Kit</div>
                            <div className="text-sm text-gray-400">Includes all logo variations, app icons, and font files in SVG/PNG/TTF formats.</div>
                        </div>
                        <button className="flex items-center gap-2 px-6 py-3 bg-white text-[#0A0B0F] rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors shadow-lg whitespace-nowrap">
                            <Download size={18} /> Download Assets
                        </button>
                    </div>
                </Card>
            </section>
        </div>
    );
};
